// -----------------------------------------------------------------------------
// src/components/dashboard/CreatorsView.js        2025-06-30
// Self-fetches via client.users.fetchSupervisedCreators()
// -----------------------------------------------------------------------------
import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Avatar,
  alpha,
  useTheme,
  ListItemAvatar,
  Grid as MuiGrid,
} from '@mui/material';
import { RequestPage, Payments } from '@mui/icons-material';
import { useQuery } from 'react-query';

import client from '../../../API';
import SettingsIcon from '@mui/icons-material/Settings';
import CreatorSettingsPanel from './components/creatorSettings';

/* ───────── helpers ───────── */
const money = (n = 0) =>
  `$${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StatusChip = ({ status = '' }) => {
  const theme = useTheme();
  const statusColors = {
    completed: theme.palette.success.main,
    pending: theme.palette.warning.main,
    requested: theme.palette.info.main,
    cancelled: theme.palette.error.main,
    active: theme.palette.success.main,
    inactive: theme.palette.text.secondary,
    archived: theme.palette.error.light,
  };
  const key = (status || '').toLowerCase();
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: alpha(statusColors[key] || theme.palette.divider, 0.2),
        color: statusColors[key] || theme.palette.text.primary,
        fontWeight: 500,
        textTransform: 'capitalize',
      }}
    />
  );
};

const TabPanel = ({ children, value, index, ...rest }) => (
  <div hidden={value !== index} {...rest}>
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

/* ───────── main component ───────── */
export default function CreatorsView({ searchQuery = '' }) {
  /* ───────── fetch creators ───────── */
  const {
    data: rawItems = [],
    isLoading: fetching,
    error: fetchError,
  } = useQuery(
    'creators',
    () => client.users.fetchSupervisedCreators(),
    { staleTime: 60_000 }
  );

  /* apply global search filter */
  const items = rawItems.filter((cr) =>
    cr.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* dialog state */
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* tab & filter state */
  const [tab, setTab] = useState(0); // 0 = Activities | 1 = Payments
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('All');

  /* responsive */
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* derived lists (after report is loaded) */
  const visibleProjects = report?.projects ?? [];
  const visiblePayments = report?.payments ?? [];

  const projectStatusOptions = [...new Set(visibleProjects.map((p) => p.status))];
  const typeOptions = [...new Set(visibleProjects.map((p) => p._type))];
  const invoiceStatusOptions = [...new Set(visiblePayments.map((p) => p.status))];

  const filteredProjects = visibleProjects.filter(
    (p) =>
      (statusFilter === 'All' || p.status === statusFilter) &&
      (typeFilter === 'All' || p._type === typeFilter)
  );

  /* ───────── handlers ───────── */
  const handleOpen = async (item) => {
    setSelected(item);
    setLoading(true);
    setError(null);
    try {
      const data = await client.creators.activityReport(
        item.creator_id ?? item.id
      );

      /* normalize payments */
      const normalizedPayments = (data?.payments ?? []).map((p) => {
        if (p.type === 'creator_invoice' || p.type === 'invoice') {
          return {
            ...p,
            status: p.invoice_status,
            date: p.invoice_created_at,
            client_full_name: p.client_name,
          };
        }
        return p;
      });

      /* normalize projects */
      const normalizedProjects = (data?.projects ?? []).map((proj) => ({
        ...proj,
        _type: proj?.source
          ? proj.source.charAt(0).toUpperCase() + proj.source.slice(1)
          : 'Unknown',
      }));

      setReport({
        ...data,
        projects: normalizedProjects,
        payments: normalizedPayments,
      });

      setStatusFilter('All');
      setTypeFilter('All');
      setInvoiceStatusFilter('All');
    } catch (err) {
      setError(err?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setReport(null);
    setError(null);
    setTab(0);
    setStatusFilter('All');
    setTypeFilter('All');
    setInvoiceStatusFilter('All');
  };

  const handleRequestPayout = (project) => {
    /* TODO – implement payout request */
    console.log('[Request Payout]', project);
  };

  /* ───────── top-level loading / error ───────── */
  if (fetching) return <CircularProgress sx={{ m: 4 }} />;
  if (fetchError)
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        {fetchError.message || 'Failed to load creators'}
      </Alert>
    );

  /* ───────── render ───────── */
  return (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {/* ---------- creator cards ---------- */}
      {items.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="info">No creators found.</Alert>
        </Grid>
      )}

      {items.map((item) => (
        <Grid key={item.id ?? item.creator_id} item xs={12} sm={6} md={4} lg={3}>
          <Card
            onClick={() => handleOpen(item)}
            sx={{
              height: '100%',
              cursor: 'pointer',
              transition: 'transform .3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: theme.shadows[6],
                background: `linear-gradient(145deg, ${alpha(
                  theme.palette.primary.light,
                  0.05
                )}, transparent)`,
              },
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Avatar
                src={item.pfphref}
                sx={{
                  width: 72,
                  height: 72,
                  mx: 'auto',
                  mb: 2,
                  boxShadow: theme.shadows[2],
                }}
              >
                {item.display_name?.[0]}
              </Avatar>

              <Typography variant="h6" fontWeight={700} gutterBottom>
                {item.display_name}
              </Typography>

              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label="Creator"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                {item.status && <StatusChip status={item.status} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* ---------- dialog ---------- */}
      <Dialog
        open={Boolean(selected)}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4 } }}
      >
        {/* header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 2,
          }}
        >
          <Avatar src={selected?.pfphref} sx={{ width: 48, height: 48 }}>
            {selected?.display_name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {selected?.display_name}
            </Typography>
            {selected?.username && (
              <Typography variant="body2" color="text.secondary">
                @{selected.username}
              </Typography>
            )}
          </Box>
          {selected?.status && <StatusChip status={selected.status} />}
        </DialogTitle>

        {/* body */}
        <DialogContent dividers sx={{ p: 0 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={48} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && report && (
            <>
              {/* tabs */}
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                <Tab icon={<RequestPage />} iconPosition="start" label="Activities" />
                <Tab icon={<Payments />} iconPosition="start" label="Payments" />
                <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
              </Tabs>

              {/* ---------- ACTIVITIES ---------- */}
              <TabPanel value={tab} index={0}>
                {/* filters */}
                <Box
                  sx={{
                    p: 2,
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="All">All Statuses</MenuItem>
                      {projectStatusOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                          <StatusChip status={s} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="All">All Types</MenuItem>
                      {typeOptions.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* list */}
                <List disablePadding>
                  {filteredProjects.map((p, idx) => {
                    const price = Number(p.price ?? 0);
                    const agencyRate = Number(p.agencyRate ?? 0);
                    const commission =
                      p._type === 'Campaign'
                        ? (price - agencyRate) * 0.2
                        : price * 0.02;
                    const completed =
                      (p.status || '').toLowerCase() === 'completed';

                    return (
                      <React.Fragment key={`${p._type}-${p.id ?? idx}`}>
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.03
                              ),
                            },
                          }}
                          secondaryAction={
                            <Button
                              variant={completed ? 'contained' : 'outlined'}
                              size="small"
                              disabled={!completed}
                              onClick={() => completed && handleRequestPayout(p)}
                              sx={{ borderRadius: 2 }}
                            >
                              {completed ? 'Request Payout' : 'Pending'}
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  mb: 1,
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {p.name}
                                </Typography>
                                <StatusChip status={p.status} />
                              </Box>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: 'grid',
                                  gap: 1,
                                  gridTemplateColumns:
                                    'repeat(auto-fit, minmax(150px, 1fr))',
                                }}
                              >
                                <Typography variant="body2">
                                  <strong>Type:</strong> {p._type}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Date:</strong>{' '}
                                  {p.date_key
                                    ? new Date(p.date_key).toLocaleDateString()
                                    : 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  <strong>Amount:</strong> {money(price)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Commission:</strong> {money(commission)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {idx < filteredProjects.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </TabPanel>

              {/* ---------- PAYMENTS ---------- */}
              <TabPanel value={tab} index={1}>
                {/* filter */}
                <Box sx={{ mb: 2, px: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={invoiceStatusFilter}
                      onChange={e =>
                        setInvoiceStatusFilter(e.target.value)
                      }
                      label="Status"
                    >
                      <MenuItem value="All">All</MenuItem>
                      {invoiceStatusOptions.map(status => (
                        <MenuItem key={status} value={status}>
                          <StatusChip status={status} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* list */}
                <List disablePadding>
                  {visiblePayments
                    .filter(
                      p =>
                        invoiceStatusFilter === 'All' ||
                        p.status === invoiceStatusFilter
                    )
                    .map((p, idx) => {
                      const amount = Number(
                        p.amount ?? p.amount_due ?? 0
                      );

                      return (
                        <React.Fragment key={`${p.id ?? idx}`}>
                          <ListItem sx={{ py: 2, px: 3 }}>
                            <ListItemAvatar>
                              {p.type === 'payout' ? (
                                <Payments />
                              ) : (
                                <RequestPage />
                              )}
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {p.type === 'payout'
                                      ? 'Payout'
                                      : 'Invoice'}
                                  </Typography>
                                  <StatusChip status={p.status} />
                                </Box>
                              }
                              secondary={
                                <MuiGrid
                                  container
                                  spacing={1}
                                  sx={{ mt: 1 }}
                                >
                                  <MuiGrid item xs={6}>
                                    <Typography variant="body2">
                                      <strong>Amount:</strong>{' '}
                                      {money(amount)}
                                    </Typography>
                                  </MuiGrid>
                                  <MuiGrid item xs={6}>
                                    <Typography variant="body2">
                                      <strong>Date:</strong>{' '}
                                      {p.date
                                        ? new Date(
                                            p.date
                                          ).toLocaleDateString()
                                        : 'N/A'}
                                    </Typography>
                                  </MuiGrid>
                                  {p.client_full_name && (
                                    <MuiGrid item xs={6}>
                                      <Typography variant="body2">
                                        <strong>Client:</strong>{' '}
                                        {p.client_full_name}
                                      </Typography>
                                    </MuiGrid>
                                  )}
                                </MuiGrid>
                              }
                            />
                          </ListItem>
                          {idx < visiblePayments.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                </List>
               </TabPanel>              {/* ---------- Settings ---------- */}
              <TabPanel value={tab} index={2}>
                <CreatorSettingsPanel
                  creator={selected}
                  onUpdated={(changes) =>
                    setSelected((prev) => ({ ...prev, ...changes }))
                  }
                />
              </TabPanel>
            </>
          )}
        </DialogContent>

        {/* footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
